// Phase 4d — TanStack Query bindings for the Modification Center.
//
// The roster (a Program's Modification change-sets) + the commit. The per-item
// review/resolve/route reuses the award-comparison hooks (the change-item RPCs
// are generic over change_set_id / item id), so this file only adds what's
// modification-specific. All route through zou's ProjectDataService.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { projectDataClient } from '../../../../lib/connect/clients';
export const PROGRAM_CHANGE_SETS_KEY = 'program-change-sets';
// Mirrors the key the award-comparison hooks invalidate on (awardCompareHooks).
const CHANGE_SET_DISCREPANCIES_KEY = 'change-set-discrepancies';
/**
 * List ALL of a Program's change-sets (newest first) for the Center roster.
 * Unfiltered by type: reverie's contract pipeline stages a Modification set
 * when a modification document is processed, and pangaea's converging
 * re-extraction sync stages a ReExtraction set of proposed contact/company
 * changes. The Center narrows to the types it reviews (never AwardImport,
 * which has its own surface at PreAward/AwardComparisonPage.tsx).
 */ export function useProgramModificationChangeSets(programId, tenantId) {
    return useQuery({
        queryKey: [
            PROGRAM_CHANGE_SETS_KEY,
            programId
        ],
        enabled: !!programId && !!tenantId,
        queryFn: async ()=>{
            const resp = await projectDataClient.listProgramChangeSets({
                programId,
                tenantId,
                changeSetType: ''
            });
            return resp.changeSets;
        }
    });
}
/**
 * CommitModification — runs the invariant gate then applies the set against the
 * already-linked Program via pangaea ApplyChangeSet. Returns `summary` (success)
 * or `violations` (gate rejected); callers branch on that.
 */ export function useCommitModification() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            return projectDataClient.commitModification({
                changeSetId: args.changeSetId
            });
        },
        onSuccess: (_resp, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    PROGRAM_CHANGE_SETS_KEY
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    CHANGE_SET_DISCREPANCIES_KEY,
                    vars.changeSetId
                ]
            });
        },
        onError: (err)=>{
            // A refused apply is its own sentence (FailedPrecondition, SAD-691):
            // rawMessage, never the "[code]" prefix or a chain.
            toast.error(apiErrorMessage(err, 'Failed to commit modification'));
        }
    });
}
